import { formatDateOrDash } from "utils/util-functions";
import { COLUMN_TYPE } from "utils/constants";
import { TableColumnTypes } from "pages/configs/configs";

export interface eiInterfaceTypes {
  availableTypes: string[];
  isEnabled: boolean;
  port: number;
  rxBytes: number;
  txBytes: number;
  type: string;
}

interface eiLocalApiTypes {
  eiRebootIsEnabled: boolean;
  modemPowerCycle: boolean;
  modemStatusIsEnabled: boolean;
}

interface UptimeTypes {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface DeviceDetailDataTypes {
  actionJson: string;
  attempts: number;
  completed: string;
  eiSerialNumber: string;
  maxAttempts: number;
  sequence: number;
}

export interface DeviceValueTypes {
  updateAvailable: boolean;
  updateStatus?: string;
  RxPower: string;
  apn: string;
  availableSW: string[];
  deviceAlias: string;
  deviceDescription: string;
  devicePool: string;
  eiConfigFile: string;
  eiInterfaces: eiInterfaceTypes[];
  eiLocalApis: eiLocalApiTypes;
  eiSerialNumber: string | undefined;
  eiSoftwareVersion: string;
  imei: string;
  lanIpAddress: string;
  lastCheckIn: string;
  locationTrailEnabled: true;
  online: false;
  organization: string;
  phoneNumber: string;
  rxBytes: number;
  ssid: string;
  txBytes: number;
  txPower: string;
  uptime: UptimeTypes;
  userName: string;
  wwanIpAddress: string;
  mac: string;
  netAccessMode: string;
  modemFirmwareVersion?: string;
  modemModel?: string;
  modemBand?: string;
  availableModemFirmware?: Array<{
    fileName: string;
    targetBand: string;
    label: string;
  }>;
  modemFirmwareTarget?: string;
  firmwarePackageSelection?: string;
}

export interface ColumnsOptionTypes {
  label: string;
  // TODO fix value type
  value: any;
}

export interface DeviceColumnTypes extends TableColumnTypes {
  width?: number;
  options?: ColumnsOptionTypes[];
  filter?: boolean;
  isSort?: boolean;
  type?: string;
  format?: (arg: any) => any;
  align?: "center" | "left" | "right" | "inherit" | "justify" | undefined;
  className?: string;
}

const DASHBOARD_COLUMNS: DeviceColumnTypes[] = [
  { id: "", label: "", width: 30 },
  {
    id: "online",
    label: "Status",
    filter: true,
    width: 50,
    isSort: true,
    type: COLUMN_TYPE.OPTION,
    options: [
      { value: true, label: "Online" },
      { value: false, label: "Offline" },
    ],
  },
  { id: "eiSerialNumber", label: "Serial No", width: 100, filter: true, isSort: true, type: COLUMN_TYPE.STRING },
  { id: "eiSoftwareVersion", label: "Version", width: 80, filter: true, isSort: true, type: COLUMN_TYPE.STRING },
  {
    id: "updateAvailable",
    label: "Update Available",
    width: 80,
    align: "left",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.OPTION,
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
  },
  {
    id: "imei",
    label: "IMEI",
    width: 110,
    align: "left",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.STRING,
  },
  {
    id: "userName",
    label: "User Name",
    filter: true,
    isSort: true,
    width: 80,
    type: COLUMN_TYPE.STRING,
  },
  {
    id: "phoneNumber",
    label: "SIM Phone No.",
    filter: true,
    width: 100,
    isSort: true,
    type: COLUMN_TYPE.STRING,
  },
  { id: "deviceAlias", label: "Alias", width: 30, isSort: true, filter: true, type: COLUMN_TYPE.STRING },
  {
    id: "locationTrailEnabled",
    label: "Location Tracking",
    format: (value: string) => (value ? "Enabled" : "Disabled"),
    filter: true,
    width: 80,
    isSort: true,
    type: COLUMN_TYPE.OPTION,
    options: [
      { value: true, label: "Enabled" },
      { value: false, label: "Disabled" },
    ],
  },
  {
    id: "rxBytes",
    label: "Received MB",
    width: 80,
    className: "hide-mobile",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.NUMBER,
  },
  {
    id: "txBytes",
    label: "Transmitted MB",
    width: 80,
    className: "hide-mobile",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.NUMBER,
  },
  {
    id: "lastCheckIn",
    label: "Last\u00a0Check\u00a0In",
    width: 120,
    align: "left",
    className: "hide-mobile",
    format: (value: string) => formatDateOrDash(value),
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.DATE,
  },
  {
    id: "devicePool",
    label: "Device\u00a0Pool",
    width: 60,
    align: "left",
    className: "hide-mobile",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.STRING,
  },
];

export const READ_ONLY_DASHBOARD_COLUMNS: DeviceColumnTypes[] = [
  { id: "", label: "", width: 15 },
  {
    id: "online",
    label: "Status",
    filter: true,
    width: 50,
    isSort: true,
    type: COLUMN_TYPE.OPTION,
    options: [
      { value: true, label: "Online" },
      { value: false, label: "Offline" },
    ],
  },
  { id: "eiSerialNumber", label: "Serial No", width: 100, filter: true, isSort: true, type: COLUMN_TYPE.STRING },
  { id: "eiSoftwareVersion", label: "Version", width: 80, filter: true, isSort: true, type: COLUMN_TYPE.STRING },
  {
    id: "updateAvailable",
    label: "Update Available",
    width: 80,
    align: "left",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.OPTION,
    options: [
      { value: true, label: "Yes" },
      { value: false, label: "No" },
    ],
  },
  {
    id: "imei",
    label: "IMEI",
    width: 110,
    align: "left",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.STRING,
  },
  {
    id: "userName",
    label: "User Name",
    filter: true,
    isSort: true,
    width: 120,
    type: COLUMN_TYPE.STRING,
  },
  {
    id: "phoneNumber",
    label: "SIM Phone No.",
    filter: true,
    width: 100,
    isSort: true,
    type: COLUMN_TYPE.STRING,
  },
  { id: "deviceAlias", label: "Alias", width: 30, isSort: true, filter: true, type: COLUMN_TYPE.STRING },
  {
    id: "locationTrailEnabled",
    label: "Location Tracking",
    format: (value: string) => (value ? "Enabled" : "Disabled"),
    filter: true,
    width: 80,
    isSort: true,
    type: COLUMN_TYPE.OPTION,
    options: [
      { value: true, label: "Enabled" },
      { value: false, label: "Disabled" },
    ],
  },
  {
    id: "rxBytes",
    label: "Received MB",
    width: 80,
    className: "hide-mobile",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.NUMBER,
  },
  {
    id: "txBytes",
    label: "Transmitted MB",
    width: 80,
    className: "hide-mobile",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.NUMBER,
  },
  {
    id: "lastCheckIn",
    label: "Last\u00a0Check\u00a0In",
    width: 180,
    align: "left",
    className: "hide-mobile",
    format: (value: string) => formatDateOrDash(value),
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.DATE,
  },
  {
    id: "devicePool",
    label: "Device\u00a0Pool",
    width: 80,
    align: "left",
    className: "hide-mobile",
    filter: true,
    isSort: true,
    type: COLUMN_TYPE.STRING,
  },
];

export default DASHBOARD_COLUMNS;
